"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";

import {
  configuredAdVendors,
  loadAdTags,
  revokeAdTags,
  trackAdPageView,
} from "@/lib/ad-tags";
import {
  CONSENT_OPEN_EVENT,
  clearAdCookies,
  openConsentSettings,
  readConsent,
  writeConsent,
  type ConsentChoice,
} from "@/lib/consent";

const VENDORS = configuredAdVendors();

/** "Meta", "Meta and Google": read as a sentence, never a list with a slash. */
function vendorPhrase(vendors: string[]): string {
  if (vendors.length <= 1) return vendors[0] ?? "";
  return `${vendors.slice(0, -1).join(", ")} and ${vendors[vendors.length - 1]}`;
}

/**
 * The cookie question, and the only thing on the site allowed to start the ad tags.
 *
 * WHAT IT DOES. On a first visit it asks. "Accept" loads the Meta Pixel and the Google tag and
 * remembers the answer; "Reject" loads nothing and remembers that. Both buttons are the same size,
 * weight and colour on purpose: the ICO's guidance is that saying no must be as easy as saying
 * yes, and a bright Accept next to a grey Reject is the pattern it names. The footer's "Cookie
 * settings" brings the question back at any time. Withdrawing clears the tags' cookies and
 * reloads, because a script that has already run cannot be unloaded any other way.
 *
 * WHY IT RENDERS NOTHING ON THE SERVER. The answer lives in the visitor's own storage, so the
 * server cannot know whether to draw the banner. Drawing it and then removing it would flash on
 * every page for everyone who already answered; drawing it only after mount does not.
 *
 * SAFARI. Fixed chrome over moving content: solid near-opaque fill, no backdrop blur, and it
 * moves with transform only (see tom-design safari-sticky-scroll-shake-fix).
 */
export function CookieConsent() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(false);
  const firstButtonRef = useRef<HTMLButtonElement>(null);
  const lastPathRef = useRef<string | null>(null);
  const reopenedRef = useRef(false);

  useEffect(() => {
    if (!VENDORS.length) return;
    const saved = readConsent();
    if (saved === "granted") loadAdTags();
    // Deferred a frame so the slide-in has a starting position to animate from.
    else if (saved === null) requestAnimationFrame(() => setOpen(true));

    const reopen = () => {
      reopenedRef.current = true;
      setOpen(true);
    };
    window.addEventListener(CONSENT_OPEN_EVENT, reopen);
    return () => window.removeEventListener(CONSENT_OPEN_EVENT, reopen);
  }, []);

  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => {
      setShown(true);
      // Only take focus when the visitor asked for the banner. On a first visit it must not
      // pull the keyboard away from whatever they were about to do.
      if (reopenedRef.current) firstButtonRef.current?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [open]);

  /* Client-side navigation does not reload the page, so the tags would only ever see the
     landing page. The first path is skipped: loadAdTags() sends that one itself. */
  useEffect(() => {
    if (lastPathRef.current === null) {
      lastPathRef.current = pathname;
      return;
    }
    if (lastPathRef.current === pathname) return;
    lastPathRef.current = pathname;
    trackAdPageView();
  }, [pathname]);

  if (!VENDORS.length || !open) return null;

  const decide = (choice: ConsentChoice) => {
    const previous = readConsent();
    writeConsent(choice);
    setShown(false);
    setOpen(false);
    reopenedRef.current = false;
    if (choice === "granted") {
      loadAdTags();
      return;
    }
    if (previous === "granted") {
      revokeAdTags();
      clearAdCookies();
      window.location.reload();
    }
  };

  const vendors = vendorPhrase(VENDORS);

  /*
   * THE SLIM BAR, on the DM resource pages only (/get/*, 2026-10-16). Those pages are full-screen
   * cards opened from Instagram on a phone, and the usual panel covered half the first card. The
   * choice is exactly the same one: two equal buttons, nothing non-essential loads until "Accept",
   * and a link to what the cookies do. Only the size changes. It sits above the card's back and
   * next buttons rather than over the text.
   */
  if (pathname?.startsWith("/get/")) {
    return (
      <section
        aria-labelledby="cookie-consent-title"
        className={`fixed inset-x-0 bottom-0 z-[60] border-t border-white/[0.12] bg-[#0f1a2c] px-4 pb-[calc(0.6rem+env(safe-area-inset-bottom))] pt-2.5 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform motion-reduce:transition-none ${
          shown ? "translate-y-0" : "translate-y-[120%]"
        }`}
      >
        <div className="mx-auto flex max-w-[34rem] items-center gap-3">
          <p className="min-w-0 flex-1 text-copy leading-[19px] text-white/80">
            <span id="cookie-consent-title">Ad cookies, only if you say yes.</span>{" "}
            <Link href="/privacy#cookies" className="underline underline-offset-2 hover:text-white">
              What they do
            </Link>
          </p>
          <button
            ref={firstButtonRef}
            type="button"
            onClick={() => decide("denied")}
            className="ownly-control inline-flex h-10 shrink-0 items-center rounded-full border border-white/[0.18] bg-white/[0.07] px-4 text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ownly-accent"
          >
            Reject
          </button>
          <button
            type="button"
            onClick={() => decide("granted")}
            className="ownly-control inline-flex h-10 shrink-0 items-center rounded-full border border-white/[0.18] bg-white/[0.07] px-4 text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ownly-accent"
          >
            Accept
          </button>
        </div>
      </section>
    );
  }

  return (
    <section
      aria-labelledby="cookie-consent-title"
      className={`fixed inset-x-0 bottom-0 z-[60] px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform motion-reduce:transition-none sm:px-5 sm:pb-5 lg:inset-x-auto lg:left-8 lg:bottom-8 lg:w-[27rem] lg:px-0 lg:pb-0 ${
        shown ? "translate-y-0" : "translate-y-[120%]"
      }`}
    >
      <div className="relative overflow-hidden rounded-2xl border border-white/[0.12] bg-[#0f1a2c] p-5 shadow-[0_28px_70px_-22px_rgba(0,0,0,0.9),0_0_0_1px_rgba(43,127,255,0.06)] sm:p-6">
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-ownly-accent/60 to-transparent"
          aria-hidden
        />
        <h2 id="cookie-consent-title" className="ownly-display ownly-small-title text-white">
          Cookies for our ads, only with your yes
        </h2>
        <p className="mt-2.5 text-copy leading-[22px] text-white/72">
          {vendors} cookies let us see which of our ads lead to someone booking a call, so we stop
          paying for the ones that do not. Nothing from {vendors} loads unless you accept.{" "}
          <Link
            href="/privacy#cookies"
            className="font-medium text-ownly-accent-text underline decoration-ownly-accent/35 underline-offset-2 transition-colors hover:text-[#5ca3ff]"
          >
            What they do
          </Link>
        </p>
        <div className="mt-5 grid grid-cols-2 gap-2.5">
          <button
            ref={firstButtonRef}
            type="button"
            onClick={() => decide("denied")}
            className="ownly-control inline-flex h-11 items-center justify-center rounded-full border border-white/[0.18] bg-white/[0.07] px-5 text-white transition-colors duration-300 hover:border-white/35 hover:bg-white/[0.12] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ownly-accent focus-visible:ring-offset-2 focus-visible:ring-offset-[#0f1a2c]"
          >
            Reject
          </button>
          <button
            type="button"
            onClick={() => decide("granted")}
            className="ownly-control inline-flex h-11 items-center justify-center rounded-full border border-white/[0.18] bg-white/[0.07] px-5 text-white transition-colors duration-300 hover:border-white/35 hover:bg-white/[0.12] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ownly-accent focus-visible:ring-offset-2 focus-visible:ring-offset-[#0f1a2c]"
          >
            Accept
          </button>
        </div>
      </div>
    </section>
  );
}

/**
 * The footer's way back to the question. Hidden when there is nothing to ask about. VENDORS is
 * fixed at build time, so the server and the browser agree and there is no hydration mismatch.
 */
export function CookieSettingsButton({ className }: { className?: string }) {
  if (!VENDORS.length) return null;
  return (
    <button type="button" onClick={openConsentSettings} className={className}>
      Cookie settings
    </button>
  );
}
